<template>
  <el-form
    v-if="filterFields.length"
    size="small"
    label-position="top"
    class="filter-form"
  >
    <div
      v-if="isProcessing"
      class="filter-form__progress-bar"
    />
    <el-row
      :gutter="20"
      type="flex"
      class="filter-form__row"
    >
      <el-col
        v-for="(filter, index) in filterFields"
        :key="index"
        :span="filter.type === 'dateRange' ? 4 : 3"
      >
        <el-form-item>
          <span slot="label">
            {{ $t(`filters.${filter.label}`) }}
            <InfoTooltip
              v-if="filter.help"
              :tooltip-content="$t(filter.help)"
            />
          </span>

          <el-input
            v-if="filter.type === 'text'"
            v-model="filterModel[filter.name]"
            type="text"
            @change="
              (value) => {
                addFilterData({
                  name: filter.name,
                  item: { key: value, label: value },
                });
              }
            "
          />

          <el-input
            v-if="filter.type === 'number'"
            v-model="filterModel[filter.name]"
            type="number"
            :min="filter.min"
            :max="filter.max"
            @change="
              (value) => {
                addFilterData({
                  name: filter.name,
                  item: { key: value, label: value },
                });
              }
            "
          />

          <el-select
            v-if="filter.type === 'select'"
            v-model="filterModel[filter.name]"
            :filterable="!!filter.filterable"
            placeholder=""
            clearable
            @change="
              (item) => {
                addFilterData({
                  name: filter.name,
                  item: { key: item.value, label: $t(`${item.label}`) },
                });
              }
            "
          >
            <el-option
              v-for="option in filter.options"
              :key="option.value"
              :label="$t(`${option.label}`)"
              :value="option"
            />
          </el-select>

          <el-date-picker
            v-if="filter.type === 'dateDay' || filter.type === 'dateString'"
            v-model="filterModel[filter.name]"
            type="date"
            placeholder=""
            :format="filter.dateFormat"
            value-format="yyyy-MM-dd"
            @change="
              (value) => {
                addFilterData({
                  name: filter.name,
                  item: { key: value, label: value },
                });
              }
            "
          />

          <el-date-picker
            v-if="filter.type === 'dateRange'"
            v-model="filterModel[filter.name]"
            type="daterange"
            range-separator="-"
            format="dd-MM-yyyy"
            value-format="yyyy-MM-dd"
            :start-placeholder="$t('filters.startDate')"
            :end-placeholder="$t('filters.endDate')"
            @change="
              (value) => {
                addFilterData({
                  name: filter.name,
                  item: {
                    key: value,
                    label: `${value}`,
                  },
                });
              }
            "
          />

          <el-switch
            v-if="filter.type === 'toggle'"
            v-model="filterModel[filter.name]"
            @change="
              (value) => {
                handleToggle(filter, value);
              }
            "
          />

          <StabilityFilter
            v-if="filter.type === 'stability'"
            :value="stabilityValue(filter.name)"
            @change="(value) => setStabilityFilter(filter.name, value)"
          />

          <el-autocomplete
            v-if="filter.type === 'autocomplete'"
            v-model="filterModel[filter.name]"
            value-key="label"
            clearable
            :fetch-suggestions="filter.autocompleteSearch"
            :trigger-on-focus="false"
            :popper-class="filter.popperClass"
            :placeholder="filter.placeholder || ''"
            @select="
              (item) => {
                handleSelectAutocomplete(item, filter.name);
                addFilterData({
                  name: filter.name,
                  item: { key: item.value, label: item.label },
                });
              }
            "
          >
            <template slot-scope="{ item }">
              <AutocompleteItem :item="item" />
            </template>
          </el-autocomplete>
        </el-form-item>

        <FilterTags
          :filter="filter"
          :filter-data="filterData[filter.name]"
          @tagClosed="
            ({ name, item }) => {
              removeFilterData({ name, item });
            }
          "
        />
      </el-col>

      <el-col
        :span="3"
        class="filter-form-buttons"
      >
        <div class="filter-form-buttons__container">
          <el-form-item>
            <el-button
              type="primary"
              size="small"
              class="search-button"
              :class="{ 'search-button--pulse': searchButtonPulse }"
              @click="searchData"
            >
              {{ $t('filters.search') }}
            </el-button>
          </el-form-item>
          <el-form-item>
            <el-button
              type="danger"
              size="small"
              @click="resetFilterData"
            >
              {{ $t('filters.clearFilter') }}
            </el-button>
          </el-form-item>
          <el-form-item>
            <i
              class="el-icon-loading filter-spinner"
              :class="{ 'filter-spinner--hidden': !isProcessing }"
            />
          </el-form-item>
        </div>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
  import { mapGetters } from 'vuex';
  import FilterTags from '@/components/FilterTags.vue';
  import InfoTooltip from '@/components/InfoTooltip.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';
  import StabilityFilter from '@/components/StabilityFilter.vue';
  import {
    IS_STABILITY_BY_FILTER,
    STABILITY_FILTER_OPTIONS,
    stabilityFilterFromIsStability,
  } from '@/config/stabilityFilter';

  export default {
    name: 'Filters',
    components: {
      FilterTags,
      InfoTooltip,
      AutocompleteItem,
      StabilityFilter,
    },
    props: {
      filters: { type: Array, default: () => [] },
      filterKey: { type: String, default: '' },
      initialData: { type: Object, default: () => {} },
      hasPermissions: { type: Boolean, default: () => true },
      loading: { type: Boolean, default: false },
    },
    data() {
      return {
        filterModel: {},
        filterData: { ...this.initialData },
        filterFields: [],
        searchButtonPulse: false,
      };
    },
    computed: {
      ...mapGetters(['userPermissions']),
      isProcessing() {
        return this.loading;
      },
    },
    watch: {
      filterData: {
        handler() {
          // Trigger pulse whenever filter data changes (add or remove)
          this.triggerSearchButtonPulse();
        },
        deep: true,
      },
    },
    created() {
      // check if use permissions
      if (this.hasPermissions) {
        // filter columns by permissions
        this.filterFields = Object.values(this.filters).reduce(
          (offersFiltersAccumulator, currentFilter) => {
            if (
              currentFilter.permissions === undefined ||
              currentFilter.permissions.length === 0 ||
              currentFilter.permissions.filter((value) =>
                [...this.userPermissions].includes(value)
              ).length > 0
            ) {
              return [...offersFiltersAccumulator, currentFilter];
            }
            return offersFiltersAccumulator;
          },
          []
        );
      } else {
        this.filterFields = [...this.filters];
      }
    },
    methods: {
      addFilterData({ name, item }) {
        // check if exist property customizeQueryString
        const currentFilter = this.filterFields.find(
          (filter) => filter.name === name
        );
        if (currentFilter && currentFilter.customizeQueryString) {
          // eslint-disable-next-line no-param-reassign
          item.key = currentFilter.customizeQueryString(item.key);
        }

        // Create property in filterData if missing
        if (!(name in this.filterData)) {
          this.$set(this.filterData, name, []);
        }

        // Add item in filterData[name] if missing
        if (
          !this.filterData[name].find((filterDataItem) => {
            if (
              filterDataItem.key instanceof Date &&
              item.key instanceof Date
            ) {
              return filterDataItem.key.getTime() === item.key.getTime();
            }
            return filterDataItem.key === item.key;
          })
        ) {
          this.filterData[name] = [...this.filterData[name], item];
        }

        // clean filterModel
        this.filterModel = {};
      },
      removeFilterData({ name, item }) {
        // remove item from filterData
        this.filterData[name] = this.filterData[name].filter(
          (filterDataItem) => {
            if (
              filterDataItem.key instanceof Date &&
              item.key instanceof Date
            ) {
              return filterDataItem.key.getTime() !== item.key.getTime();
            }
            return filterDataItem.key !== item.key;
          }
        );

        // toggle filters keep their on/off state in filterModel
        // independently of filterData, so it must be reset explicitly
        const currentFilter = this.filterFields.find(
          (filter) => filter.name === name
        );
        if (currentFilter && currentFilter.type === 'toggle') {
          this.$set(this.filterModel, name, false);
          this.requestData();
        }
      },
      requestData() {
        // Catch needed: see https://github.com/vuejs/vue-router/issues/2872#issuecomment-522341874
        const { page, ...queryWithoutPage } = this.$route.query;
        this.$router
          .replace({
            query: {
              ...queryWithoutPage,
              page: 1, // Fix for searching an element outside of the current page
              filterKey: this.filterKey,
              filterData: JSON.stringify(this.filterData),
            },
          })
          .catch(() => {});
      },
      searchData() {
        // Trigger immediate search with current filter values
        this.requestData();
        this.$emit('search');
      },
      resetFilterData() {
        this.filterModel = {};
        this.filterData = {};

        // Trigger immediate search when clearing filters
        this.requestData();
      },
      triggerSearchButtonPulse() {
        // Force animation restart by toggling class
        this.searchButtonPulse = false;
        // Use setTimeout instead of nextTick to ensure animation retriggers
        setTimeout(() => {
          this.searchButtonPulse = true;
        }, 10);
      },
      stabilityValue(name) {
        const [item] = this.filterData[name] || [];
        return stabilityFilterFromIsStability(item && item.key);
      },
      setStabilityFilter(name, value) {
        if (!(value in IS_STABILITY_BY_FILTER)) {
          this.$set(this.filterData, name, []);
          return;
        }
        const option = STABILITY_FILTER_OPTIONS.find((o) => o.value === value);
        this.$set(this.filterData, name, [
          { key: IS_STABILITY_BY_FILTER[value], label: this.$t(option.label) },
        ]);
      },
      handleSelectAutocomplete(item, name) {
        this.filterModel[name] = item.value;
      },
      handleToggle(filter, value) {
        if (value) {
          this.addFilterData({
            name: filter.name,
            item: {
              key: filter.activeValue ?? true,
              label: filter.activeLabel
                ? this.$t(filter.activeLabel)
                : filter.name,
            },
          });
          this.$set(this.filterModel, filter.name, true);
        } else {
          this.filterData[filter.name] = [];
          this.requestData();
        }
      },
    },
  };
</script>

<style lang="scss">
  .filter-form {
    padding-bottom: 20px;
    min-height: $filter-form-min-height;
    position: relative;

    &__progress-bar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(
        90deg,
        $--color-primary 0%,
        $--color-primary 50%,
        transparent 50%,
        transparent 100%
      );
      background-size: 200% 100%;
      animation: progressBarAnimation 1.5s linear infinite;
      z-index: 9999;
    }

    &__row {
      flex-wrap: wrap;
    }

    .filter-form-buttons {
      display: flex;

      &__container {
        display: flex;
        // align-items: center;
        margin-top: $filter-label-min-height;

        .el-form-item {
          .el-form-item__content {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
        }

        .filter-spinner {
          margin-left: 0.5rem;
          margin-right: 0.5rem;
          font-size: 1.5rem;
          color: $--color-primary;
          display: inline-flex;
          align-items: center;

          &--hidden {
            visibility: hidden;
          }
        }
      }
    }

    .el-form-item {
      &__label {
        color: $--color-text-regular;
        min-height: $filter-label-min-height;
      }

      &--small {
        .el-form-item {
          &__label {
            padding-bottom: 15px;
            line-height: 1;
            min-height: $filter-label-min-height;
          }
        }
      }

      .el-autocomplete,
      .el-date-editor {
        width: 100%;
      }

      .el-range-input {
        background-color: transparent;
      }
    }
  }

  .search-button {
    position: relative;

    &--pulse {
      animation: searchButtonPulse 2s ease-out;
    }
  }

  @keyframes searchButtonPulse {
    0% {
      box-shadow: 0 0 0 0 rgba($--color-primary, 0.7);
    }
    50% {
      box-shadow: 0 0 0 30px rgba($--color-primary, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba($--color-primary, 0);
    }
  }

  @keyframes progressBarAnimation {
    0% {
      background-position: 0% 0%;
    }
    100% {
      background-position: 200% 0%;
    }
  }
</style>
